import { useQuery, useQueryClient } from '@tanstack/react-query';
import { demandesAchatAPI, DemandeAchatFilters } from '../../services/api';
import { DemandeAchat, DemandeAchatListResponse } from '../../types/demandeAchat';

/**
 * Query hooks for Demandes d'Achat (server-paginated list + deleted list).
 *
 * Key root MUST be 'demande-achats' — the slug the realtime-invalidation
 * middleware broadcasts for /api/demande-achats — so another user's change
 * auto-refreshes this list. Invalidating the root key refreshes every
 * paginated/filtered variant and the deleted list at once.
 */
export interface DemandeAchatsListParams {
  skip: number;
  limit: number;
  search?: string;
  filters?: DemandeAchatFilters;
}

export const demandeAchatsKeys = {
  root: ['demande-achats'] as const,
  list: (params: DemandeAchatsListParams) => ['demande-achats', 'list', params] as const,
  deleted: ['demande-achats', 'deleted'] as const,
};

export function useDemandeAchatsList(params: DemandeAchatsListParams, enabled: boolean) {
  return useQuery<DemandeAchatListResponse>({
    queryKey: demandeAchatsKeys.list(params),
    queryFn: () =>
      demandesAchatAPI.getPaginated(
        params.skip,
        params.limit,
        params.search,
        params.filters,
      ),
    enabled,
    keepPreviousData: true,
  });
}

export function useDeletedDemandeAchats(enabled: boolean) {
  return useQuery<DemandeAchat[]>({
    queryKey: demandeAchatsKeys.deleted,
    queryFn: () => demandesAchatAPI.getDeleted(),
    enabled,
  });
}

export function useInvalidateDemandeAchats() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: demandeAchatsKeys.root });
}
